// Step-up 2FA verification modal. Fires `/auth/2fa/stepup` and calls
// onVerified with the resulting stepup_token.

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { api, ApiError } from "@/lib/api";
import type { StepUpResponse } from "@/types/admin";
import { useDict } from "@/i18n/LocaleProvider";

interface Props {
  open: boolean;
  message?: string;
  onVerified: (token: string) => void | Promise<void>;
  onClose: () => void;
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError)
    return typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
  if (err instanceof Error) return err.message;
  return String(err);
}

export function StepUpModal({ open, message, onVerified, onClose }: Props) {
  const t = useDict().app.adminStepUp;
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCode("");
      setErr(null);
    }
  }, [open]);

  if (!open) return null;

  async function verify() {
    setBusy(true);
    setErr(null);
    try {
      const r = await api<StepUpResponse>("/auth/2fa/stepup", {
        method: "POST",
        body: { code },
      });
      await onVerified(r.stepup_token);
      setCode("");
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-5"
      style={{ background: "rgba(0,0,0,0.75)", zIndex: 1000 }}
    >
      <div
        className="w-full max-w-[420px] rounded p-7"
        style={{ background: "rgb(var(--color-bg2))", border: "2px solid rgb(var(--color-accent))" }}
      >
        <div className="flex items-center gap-[10px] mb-4">
          <div className="text-xl">🔒</div>
          <div>
            <div className="font-display text-[16px] font-bold text-content">
              {t.title}
            </div>
            <div className="font-mono text-[10px] text-muted mt-[2px]">
              {t.subtitle}
            </div>
          </div>
        </div>

        {message && (
          <div
            className="px-3 py-2 rounded text-muted font-mono text-[11px] mb-[14px]"
            style={{ background: "rgb(var(--color-bg))", border: "1px solid rgb(var(--color-stroke))" }}
          >
            {message}
          </div>
        )}

        <div
          onKeyDown={(e) => {
            if (e.key === "Enter" && code && !busy) verify();
          }}
        >
          <Input
            label={t.codeLabel}
            value={code}
            onChange={setCode}
            placeholder={t.codePlaceholder}
          />
        </div>

        {err && (
          <div className="px-3 py-2 bg-danger-bg border border-danger/40 rounded text-danger font-mono text-[11px] mb-3">
            {err}
          </div>
        )}

        <div className="flex gap-2">
          <Button
            variant="primary"
            onClick={verify}
            disabled={busy || code.length < 6}
          >
            {busy ? t.busy : t.verify}
          </Button>
          <Button
            variant="default"
            onClick={() => {
              setCode("");
              setErr(null);
              onClose();
            }}
          >
            {t.cancel}
          </Button>
        </div>

        <div className="mt-3 font-mono text-[9px] text-dim text-center">
          {t.tokenValid}
        </div>
      </div>
    </div>
  );
}
